import { useQueryClient } from '@tanstack/react-query'
import { DollarSignIcon, MoreVerticalIcon, SendIcon, StarIcon, UsersIcon, UserXIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import type { GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Badge, BorderedCheckbox, Button, ButtonGroup, CheckboxGroup, IconButton } from 'slab'
import { capitalize, handleGrapeAPIFieldErrors } from 'utils'
import {
  getGetPartnersPartnerIdPartnerAdminsQueryKey,
  useDeletePartnersPartnerIdPartnerAdminsPartnerAdminId,
  usePostPartnersPartnerIdPartnerAdminsPartnerAdminIdResendInvite,
  usePutPartnersPartnerIdPartnerAdminsPartnerAdminId,
} from 'utils/web_api'
import type { PartnersPartnerAdmin } from 'utils/web_api.schemas'

import { ConfirmationDialog } from '../reusable/ConfirmationDialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet'

const ROLE_OPTIONS = [
  { value: 'producer', label: 'Producer', icon: UsersIcon },
  { value: 'finance', label: 'Finance', icon: DollarSignIcon },
  { value: 'admin', label: 'Admin', icon: StarIcon },
]

interface FormData {
  roles: string[]
}

interface PartnersTeamMemberPanelProps {
  teamMember: PartnersPartnerAdmin | null
  isOpen: boolean
  onClose: () => void
  partnerId: string
  onUpdate?: (newRoles: string[]) => void
}

const PartnersTeamMemberPanel = ({
  teamMember,
  isOpen,
  onClose,
  partnerId,
  onUpdate,
}: PartnersTeamMemberPanelProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isResendConfirmOpen, setIsResendConfirmOpen] = useState(false)
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false)
  const queryClient = useQueryClient()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isDirty, isValid }
  } = useForm<FormData>({
    defaultValues: {
      roles: []
    },
    mode: 'onChange'
  })

  const resendInviteMutation = usePostPartnersPartnerIdPartnerAdminsPartnerAdminIdResendInvite({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getGetPartnersPartnerIdPartnerAdminsQueryKey(partnerId)[0]]
        })
        setIsResendConfirmOpen(false)
        setIsPopoverOpen(false)
        toast.success(`Invite resent to ${teamMember?.first_name} ${teamMember?.last_name}`)
        onClose()
      },
      onError: (error: any) => {
        console.error('Failed to resend invite:', error)
        toast.error('Failed to resend invite. Please try again.')
      }
    }
  })

  const removeTeamMemberMutation = useDeletePartnersPartnerIdPartnerAdminsPartnerAdminId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getGetPartnersPartnerIdPartnerAdminsQueryKey(partnerId)[0]]
        })
        setIsRemoveConfirmOpen(false)
        setIsPopoverOpen(false)
        toast.success(`${teamMember?.first_name} ${teamMember?.last_name} was removed from the team`)
        onClose()
      },
      onError: (error: any) => {
        console.error('Failed to remove team member:', error)
        toast.error('Failed to remove team member. Please try again.')
      }
    }
  })

  const updateTeamMemberMutation = usePutPartnersPartnerIdPartnerAdminsPartnerAdminId({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [getGetPartnersPartnerIdPartnerAdminsQueryKey(partnerId)[0]]
        })
        reset()
        toast.success(`${teamMember?.first_name} ${teamMember?.last_name} was successfully updated`)
        const submittedRoles = (variables.data as { roles?: string[] })?.roles
        if (submittedRoles) {
          onUpdate?.(submittedRoles)
        }
        onClose()
      },
      onError: (error: GrapeAPIErrorType) => {
        console.error('Failed to update team member:', error)

        if (error.fieldErrors) {
          handleGrapeAPIFieldErrors(error, { setError } as any)
        }

        if (error.errors?.length) {
          toast.error('Failed to update team member. Please try again.')
        }
      }
    }
  })

  useEffect(() => {
    if (teamMember) {
      reset({
        roles: [...teamMember.roles]
      })
    }
  }, [teamMember, reset])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isPopoverOpen && dropdownRef.current) {
        const target = event.target as Element
        if (!dropdownRef.current.contains(target)) {
          setIsPopoverOpen(false)
        }
      }
    }

    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isPopoverOpen])

  if (!teamMember) return null

  const handleResendInvite = () => {
    setIsResendConfirmOpen(true)
    setIsPopoverOpen(false)
  }

  const handleRemoveTeamMember = () => {
    setIsRemoveConfirmOpen(true)
    setIsPopoverOpen(false)
  }

  const onSubmit = (data: FormData) => {
    updateTeamMemberMutation.mutate({
      partnerId,
      partnerAdminId: teamMember.id,
      data: {
        roles: data.roles,
      } as any
    })
  }

  const confirmResendInvite = () => {
    resendInviteMutation.mutate({
      partnerId,
      partnerAdminId: teamMember.id,
    })
  }

  const confirmRemoveTeamMember = () => {
    removeTeamMemberMutation.mutate({
      partnerId,
      partnerAdminId: teamMember.id,
    })
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side='right' className='w-[400px] sm:w-[540px] flex flex-col' aria-describedby={undefined}>
        <SheetHeader>
          <div className='flex items-center justify-between mt-2'>
            <SheetTitle className='text-left'>
              {teamMember.first_name} {teamMember.last_name}
            </SheetTitle>
            {teamMember.status !== 'active' && (
              <div className='relative' ref={dropdownRef}>
                <IconButton
                  icon={MoreVerticalIcon}
                  label='More actions'
                  onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                />
                {isPopoverOpen && (
                  <div className='absolute right-0 top-full mt-1 w-60 bg-white border border-gray-200 rounded-lg shadow-lg'>
                    <div className='p-2'>
                      <ButtonGroup layout='vertical'>
                        <Button
                          variant='tertiary'
                          leftIcon={SendIcon}
                          onClick={handleResendInvite}
                          disabled={resendInviteMutation.isPending}
                        >
                          Resend invite
                        </Button>
                        <Button
                          variant='tertiary-destructive'
                          leftIcon={UserXIcon}
                          onClick={handleRemoveTeamMember}
                          disabled={removeTeamMemberMutation.isPending}
                        >
                          Remove team member
                        </Button>
                      </ButtonGroup>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetHeader>

        <div className='flex-1'>
          {/* Team Member Details */}
          <div className='space-y-4'>
            <div>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                Email Address
              </label>
              <p className='mt-1 text-sm text-gray-900'>{teamMember.email}</p>
            </div>

            <div>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                Status
              </label>
              <p className='mt-1'>
                <Badge type={teamMember.status === 'active' ? 'success' : 'info'}>{capitalize(teamMember.status)}</Badge>
              </p>
            </div>

            <div>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block'>
                Roles
              </label>
              <Controller
                name='roles'
                control={control}
                rules={{
                  required: 'At least one role must be selected',
                  validate: (value) => value.length > 0 || 'At least one role must be selected'
                }}
                render={({ field }) => (
                  <CheckboxGroup
                    layout='vertical'
                    gap={3}
                  >
                    {ROLE_OPTIONS.map((option) => {
                      const Icon = option.icon
                      const isSelected = field.value.includes(option.value)

                      return (
                        <BorderedCheckbox
                          key={option.value}
                          checked={isSelected}
                          onChange={() => {
                            const newRoles = isSelected
                              ? field.value.filter((r: string) => r !== option.value)
                              : [...field.value, option.value]
                            field.onChange(newRoles)
                          }}
                          size='small'
                          icon={Icon}
                          label={option.label}
                        />
                      )
                    })}
                  </CheckboxGroup>
                )}
              />
              {errors.roles && (
                <p className='mt-2 text-sm text-red-600'>{errors.roles.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className='mt-auto p-6 pb-0 -mx-6 bg-white border-t border-gray-200'>
          <div className='flex flex-col space-y-2 w-full'>
            <Button
              type='button'
              variant='primary'
              size='large'
              width='full'
              onClick={handleSubmit(onSubmit)}
              disabled={!isDirty || !isValid || updateTeamMemberMutation.isPending}
            >
              {updateTeamMemberMutation.isPending ? 'Saving...' : 'Save changes'}
            </Button>
            <Button
              type='button'
              variant='secondary'
              size='large'
              width='full'
              onClick={() => {
                reset()
                onClose()
              }}
              disabled={updateTeamMemberMutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </SheetContent>

      {/* Resend Invite Confirmation Dialog */}
      <ConfirmationDialog
        open={isResendConfirmOpen}
        onOpenChange={setIsResendConfirmOpen}
        title='Resend Invite'
        description={`Are you sure you want to resend an invite to ${teamMember.first_name} ${teamMember.last_name}?`}
        submitLabel='Resend Invite'
        onSubmit={confirmResendInvite}
        isSubmitting={resendInviteMutation.isPending}
      />

      {/* Remove Team Member Confirmation Dialog */}
      <ConfirmationDialog
        open={isRemoveConfirmOpen}
        onOpenChange={setIsRemoveConfirmOpen}
        title='Remove Team Member'
        description={`Are you sure you want to remove ${teamMember.first_name} ${teamMember.last_name} from the team? This action cannot be undone.`}
        submitLabel='Remove'
        onSubmit={confirmRemoveTeamMember}
        isSubmitting={removeTeamMemberMutation.isPending}
        isDanger
      />
    </Sheet>
  )
}

export default PartnersTeamMemberPanel
